import React, { useCallback, useMemo, useState } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { Link } from 'react-router-dom';

import ArchiveIcon from '@mui/icons-material/Archive';
import ApproveIcon from '@mui/icons-material/CheckCircleOutline';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { green, grey, orange } from '@mui/material/colors';
import { DataGrid, GridColDef, GridSortModel } from '@mui/x-data-grid';

import GridPagination from '~/components/Common/Elements/GridPagination';

import BedbankService from '~/services/BedbankService';

const CONFIDENCE_OPTIONS = [
  { value: 'all', label: 'All Confidence Levels' },
  { value: 'HIGH', label: 'High Confidence' },
  { value: 'LOW', label: 'Low Confidence' },
  { value: null, label: 'No Match' },
] as const;

type ConfidenceFilter = 'all' | 'HIGH' | 'LOW' | null;
type CandidateTab = 'to_action' | 'completed' | 'archived';

const PAGE_SIZE = 50;

function getTabQueryParams(tab: CandidateTab): { isMapped?: boolean; isArchived?: boolean } {
  switch (tab) {
    case 'to_action':
      return { isMapped: false, isArchived: false };
    case 'completed':
      return { isMapped: true };
    case 'archived':
      return { isArchived: true };
    default:
      return {};
  }
}

export default function RoomMappingCandidatesPage() {
  const [selectedCandidateIds, setSelectedCandidateIds] = useState<Array<string>>([]);
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [confidenceFilter, setConfidenceFilter] = useState<ConfidenceFilter>('all');
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'roomHits', sort: 'desc' }]);
  const [activeTab, setActiveTab] = useState<CandidateTab>('to_action');

  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const queryParams = useMemo(() => getTabQueryParams(activeTab), [activeTab]);

  const {
    data: candidatesData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['roomMappingCandidates', activeTab, queryParams],
    queryFn: () => BedbankService.getRoomMappingCandidates(queryParams),
  });

  const approveMutation = useMutation({
    mutationFn: (candidateIds: Array<string>) => BedbankService.approveRoomMappingCandidates(candidateIds),
    onSuccess: (data) => {
      const { approved, failed } = data.result;

      if (approved.length > 0) {
        enqueueSnackbar(`Successfully approved ${approved.length} mapping(s)`, { variant: 'success' });
      }

      if (failed.length > 0) {
        failed.forEach((failure) => {
          enqueueSnackbar(`Failed to approve candidate: ${failure.error}`, { variant: 'error' });
        });
      }

      queryClient.invalidateQueries({ queryKey: ['roomMappingCandidates'] });
      setSelectedCandidateIds([]);
      setConfirmationDialogOpen(false);
    },
    onError: (err: Error) => {
      enqueueSnackbar(`Failed to approve mappings: ${err.message}`, { variant: 'error' });
    },
  });

  const archiveMutation = useMutation({
    mutationFn: (candidateIds: Array<string>) => BedbankService.archiveRoomMappingCandidates(candidateIds),
    onSuccess: (data) => {
      const { archived, failed } = data.result;

      if (archived.length > 0) {
        enqueueSnackbar(`Successfully archived ${archived.length} candidate(s)`, { variant: 'success' });
      }

      if (failed.length > 0) {
        failed.forEach((failure) => {
          enqueueSnackbar(`Failed to archive candidate: ${failure.error}`, { variant: 'error' });
        });
      }

      queryClient.invalidateQueries({ queryKey: ['roomMappingCandidates'] });
      setSelectedCandidateIds([]);
      setArchiveDialogOpen(false);
    },
    onError: (err: Error) => {
      enqueueSnackbar(`Failed to archive candidates: ${err.message}`, { variant: 'error' });
    },
  });

  const candidates = useMemo(() => candidatesData?.result ?? [], [candidatesData?.result]);

  const filteredCandidates = useMemo(() => {
    if (confidenceFilter === 'all') {
      return candidates;
    }
    if (confidenceFilter === null) {
      return candidates.filter((c) => c.confidence === null);
    }
    return candidates.filter((c) => c.confidence === confidenceFilter);
  }, [candidates, confidenceFilter]);

  const selectedCandidates = useMemo(() => {
    return candidates.filter((c) => selectedCandidateIds.includes(c.id));
  }, [candidates, selectedCandidateIds]);

  const canApproveCandidate = useCallback((candidate: App.Bedbank.RoomMappingCandidate) => {
    // Can only approve candidates that have an AI matched room
    return candidate.aiMatchedRoomId !== null;
  }, []);

  const canApproveSelected = useMemo(() => {
    return selectedCandidates.some((c) => canApproveCandidate(c));
  }, [selectedCandidates, canApproveCandidate]);

  const approvableCandidateIds = useMemo(() => {
    return selectedCandidates.filter((c) => canApproveCandidate(c)).map((c) => c.id);
  }, [selectedCandidates, canApproveCandidate]);

  const handleTabChange = useCallback((_: React.SyntheticEvent, newTab: CandidateTab) => {
    setActiveTab(newTab);
    setSelectedCandidateIds([]);
    setConfidenceFilter('all');
  }, []);

  const handleSelectCandidate = useCallback((candidateId: string, checked: boolean) => {
    setSelectedCandidateIds((prev) => (checked ? [...prev, candidateId] : prev.filter((id) => id !== candidateId)));
  }, []);

  const handleConfidenceFilterChange = useCallback((event: SelectChangeEvent<ConfidenceFilter>) => {
    setConfidenceFilter(event.target.value as ConfidenceFilter);
    setSelectedCandidateIds([]);
  }, []);

  const handleConfirmMapping = useCallback(() => {
    setConfirmationDialogOpen(true);
  }, []);

  const handleConfirmAction = useCallback(() => {
    approveMutation.mutate(approvableCandidateIds);
  }, [approveMutation, approvableCandidateIds]);

  const handleCancelAction = useCallback(() => {
    setConfirmationDialogOpen(false);
  }, []);

  const handleArchive = useCallback(() => {
    setArchiveDialogOpen(true);
  }, []);

  const handleConfirmArchive = useCallback(() => {
    archiveMutation.mutate(selectedCandidateIds);
  }, [archiveMutation, selectedCandidateIds]);

  const handleCancelArchive = useCallback(() => {
    setArchiveDialogOpen(false);
  }, []);

  const canSelectCandidate = useCallback(
    (_candidate: App.Bedbank.RoomMappingCandidate) => {
      // In "to_action" tab: can select any candidate for approval or archiving
      // In "completed" or "archived" tabs: selection not needed (read-only view)
      if (activeTab !== 'to_action') {
        return false;
      }
      return true; // Allow selection of any candidate for archiving
    },
    [activeTab],
  );

  const getConfidenceChip = useCallback((confidence: App.Bedbank.RoomMappingCandidateConfidence) => {
    switch (confidence) {
      case 'HIGH':
        return <Chip label="High" size="small" sx={{ bgcolor: green[100], color: green[800] }} />;
      case 'LOW':
        return <Chip label="Low" size="small" sx={{ bgcolor: orange[100], color: orange[800] }} />;
      default:
        return <Chip label="No Match" size="small" sx={{ bgcolor: grey[200], color: grey[700] }} />;
    }
  }, []);

  const selectColumn: GridColDef<App.Bedbank.RoomMappingCandidate> = useMemo(
    () => ({
      field: 'select',
      headerName: 'Select',
      width: 70,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const canSelect = canSelectCandidate(params.row);
        const isSelected = selectedCandidateIds.includes(params.row.id);

        return (
          <Box display="flex" alignItems="center" justifyContent="center" height="100%" minHeight="52px">
            <Checkbox
              checked={isSelected}
              disabled={!canSelect}
              onChange={(event) => handleSelectCandidate(params.row.id, event.target.checked)}
            />
          </Box>
        );
      },
    }),
    [canSelectCandidate, selectedCandidateIds, handleSelectCandidate],
  );

  const columns: Array<GridColDef<App.Bedbank.RoomMappingCandidate>> = useMemo(
    () => [
      ...(activeTab === 'to_action' ? [selectColumn] : []),
      {
        field: 'bedbankPropertyName',
        headerName: 'Property',
        flex: 1,
        minWidth: 200,
        renderCell: (params) => (
          <Link
            style={{ display: 'flex', alignItems: 'center', height: '100%' }}
            to={`/bedbank/properties/${params.row.bedbankPropertyId}?mappedRoomId=${params.row.supplierRoomId}#3`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {params.row.bedbankPropertyName}
          </Link>
        ),
      },
      {
        field: 'aiMatchedRoomName',
        headerName: 'Master Room',
        flex: 1,
        minWidth: 200,
        renderCell: (params) => (
          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            {params.row.aiMatchedRoomName || '-'}
          </Typography>
        ),
      },
      {
        field: 'supplierRoomName',
        headerName: 'Supplier Room',
        flex: 1,
        minWidth: 200,
        renderCell: (params) => (
          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            {params.row.supplierRoomName}
          </Typography>
        ),
      },
      {
        field: 'supplier',
        headerName: 'Supplier',
        width: 120,
        renderCell: (params) => (
          <Typography
            variant="body2"
            sx={{ textTransform: 'capitalize', display: 'flex', alignItems: 'center', height: '100%' }}
          >
            {params.row.supplier}
          </Typography>
        ),
      },
      {
        field: 'confidence',
        headerName: 'Confidence',
        width: 120,
        renderCell: (params) => (
          <Box display="flex" alignItems="center" height="100%">
            {getConfidenceChip(params.row.confidence)}
          </Box>
        ),
      },
      {
        field: 'roomHits',
        headerName: 'Hits',
        width: 100,
        sortable: true,
        renderCell: (params) => (
          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            {params.row.roomHits?.toLocaleString() ?? '-'}
          </Typography>
        ),
      },
      {
        field: 'reason',
        headerName: 'Reason',
        flex: 1.5,
        minWidth: 250,
        renderCell: (params) => (
          <Tooltip title={params.row.reason || 'No reason provided'} arrow>
            <Typography
              variant="body2"
              sx={{
                display: 'flex',
                alignItems: 'center',
                height: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {params.row.reason || '-'}
            </Typography>
          </Tooltip>
        ),
      },
    ],
    [activeTab, selectColumn, getConfidenceChip],
  );

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">
          Failed to load room mapping candidates: {error instanceof Error ? error.message : 'Unknown error'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="To Action" value="to_action" />
          <Tab label="Completed" value="completed" />
          <Tab label="Archived" value="archived" />
        </Tabs>
      </Box>

      {/* Toolbar */}
      <Box
        sx={{
          backgroundColor: 'grey.50',
          p: 2,
          mb: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderRadius: 1,
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="confidence-filter-label">Confidence Filter</InputLabel>
            <Select
              labelId="confidence-filter-label"
              value={confidenceFilter}
              label="Confidence Filter"
              onChange={handleConfidenceFilterChange}
            >
              {CONFIDENCE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="body2" color="text.secondary">
            {filteredCandidates.length} candidate{filteredCandidates.length !== 1 ? 's' : ''}
          </Typography>
        </Stack>

        {activeTab === 'to_action' && (
          <Stack direction="row" spacing={1}>
            <Tooltip
              title={
                selectedCandidateIds.length > 0 && !canApproveSelected
                  ? 'Selected candidates have no AI match to approve'
                  : ''
              }
            >
              <span>
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleConfirmMapping}
                  disabled={!canApproveSelected || approveMutation.isPending}
                  startIcon={<ApproveIcon />}
                >
                  {approveMutation.isPending ? 'Approving...' : `Approve (${approvableCandidateIds.length})`}
                </Button>
              </span>
            </Tooltip>
            <Button
              variant="outlined"
              color="warning"
              onClick={handleArchive}
              disabled={selectedCandidateIds.length === 0 || archiveMutation.isPending}
              startIcon={<ArchiveIcon />}
            >
              {archiveMutation.isPending ? 'Archiving...' : `Archive (${selectedCandidateIds.length})`}
            </Button>
          </Stack>
        )}
      </Box>

      {/* Empty State */}
      {!isLoading && filteredCandidates.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {activeTab === 'to_action' && 'No unmapped rooms requiring action for the selected filters.'}
          {activeTab === 'completed' && 'No completed mappings found.'}
          {activeTab === 'archived' && 'No archived candidates found.'}
        </Alert>
      )}

      {/* Data Grid */}
      <Box sx={{ height: '100%', width: '100%' }}>
        <DataGrid
          columns={columns}
          rows={filteredCandidates}
          loading={isLoading || approveMutation.isPending || archiveMutation.isPending}
          getRowId={(row) => row.id}
          sortModel={sortModel}
          onSortModelChange={(newModel) => setSortModel(newModel)}
          slots={{
            pagination: GridPagination,
          }}
          pageSizeOptions={[PAGE_SIZE, 100, 200]}
          initialState={{
            pagination: {
              paginationModel: { pageSize: PAGE_SIZE },
            },
          }}
          disableRowSelectionOnClick
          getRowHeight={() => 'auto'}
          sx={{
            '& .MuiDataGrid-row': {
              minHeight: '52px !important',
            },
          }}
        />
      </Box>

      {/* Approve Confirmation Dialog */}
      <Dialog open={confirmationDialogOpen} onClose={handleCancelAction} maxWidth="md" fullWidth>
        <DialogTitle>
          Confirm mapping of {approvableCandidateIds.length}{' '}
          {approvableCandidateIds.length === 1 ? 'candidate' : 'candidates'}?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            You are about to approve the following room mapping{' '}
            {approvableCandidateIds.length === 1 ? 'candidate' : 'candidates'}. This will create verified room mappings
            for the selected rooms.
          </Typography>

          {selectedCandidateIds.length > approvableCandidateIds.length && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {selectedCandidateIds.length - approvableCandidateIds.length} selected candidate(s) will be skipped as
              they have no AI match.
            </Alert>
          )}

          <Divider sx={{ my: 2 }} />

          <TableContainer sx={{ maxHeight: 300 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Supplier Room</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Master Room</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Supplier</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedCandidates
                  .filter((c) => canApproveCandidate(c))
                  .map((candidate) => (
                    <TableRow key={candidate.id}>
                      <TableCell>{candidate.supplierRoomName}</TableCell>
                      <TableCell>{candidate.aiMatchedRoomName || '-'}</TableCell>
                      <TableCell sx={{ textTransform: 'capitalize' }}>{candidate.supplier}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelAction} variant="text">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAction}
            variant="contained"
            color="success"
            disabled={approveMutation.isPending}
          >
            {approveMutation.isPending ? 'Approving...' : 'Confirm Mapping'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Archive Confirmation Dialog */}
      <Dialog open={archiveDialogOpen} onClose={handleCancelArchive} maxWidth="md" fullWidth>
        <DialogTitle>
          Archive {selectedCandidateIds.length} {selectedCandidateIds.length === 1 ? 'candidate' : 'candidates'}?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Archiving will remove these candidates from the action queue. They will no longer appear in future
            suggestions.
          </Typography>

          <Divider sx={{ my: 2 }} />

          <TableContainer sx={{ maxHeight: 300 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Supplier Room</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Property</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Supplier</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedCandidates.map((candidate) => (
                  <TableRow key={candidate.id}>
                    <TableCell>{candidate.supplierRoomName}</TableCell>
                    <TableCell>{candidate.bedbankPropertyName}</TableCell>
                    <TableCell sx={{ textTransform: 'capitalize' }}>{candidate.supplier}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelArchive} variant="text">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmArchive}
            variant="contained"
            color="warning"
            disabled={archiveMutation.isPending}
          >
            {archiveMutation.isPending ? 'Archiving...' : 'Confirm Archive'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
